import type * as H from 'history'
import type {
  match as MatchType,
  RouteComponentProps,
  RouteProps,
} from 'react-router-dom'
import type {OmitStrict} from 'type-zoo'
import type {EmptyObject} from '@kapitalist/utils/EmptyObject'

// The shape of the props that Route components rendered beneath a react-router `<Switch>` will receive
// the react-router typings don't provide this type, annoyingly. RouteProps is the shape of the props we can provide to a Route component ourselves,
// but nothing describes the shape of the props the Route component will _actually_ get at runtime (react-router inserts some stuff, namely an always-present `location` and `computedMatch`).
// I'm also fixing the fact that `location.state` is `any` in `react-router` but really might not be set at all (`any` hides `undefined` unsafely) while I'm here.
// source for this manual insertion:
// https://github.com/remix-run/react-router/blob/5e2b04018847c799e8da1a5f9cc632b6d02da152/packages/react-router/modules/Switch.js#L40
export type TPropsCustomRouteComponentsGet<
  Path extends string = string,
  ExpectedLocationStateShape = EmptyObject,
> = OmitStrict<RouteProps<Path>, 'location'> & {
  location: OmitStrict<H.Location, 'state'> & {
    state: Partial<ExpectedLocationStateShape> | undefined
  }
  computedMatch: MatchType
}

// `Route.LoggedIn` and other "Basic" route types expect this key to be missing from the props of its RouteComponent,
// and the compiler errors if it is present on the props type of e.g. the `component` of e.g. `Route.LoggedIn`.
//
// similarly, non-"Basic" routes like `Route.LoggedInRequiresOrgWithBanking` require its `component`'s props to require the
// presence of this key and a route-specific value for it. otherwise, you would be able to use a route like
// `Route.LoggedInRequiresOrgWithBanking` when the rendered component didn't seem to care about, say, being logged in w/ an org.
export const ROUTE_TYPE_KEY = '__routeType'

// no Route.LoggedIn (nor any other Route type that has no special requirements, which we're calling "Basic" here)
// component should be expecting, or allowing, a `ROUTE_TYPE_KEY` to be set, so we enforce that it is missing
// from the props of the component here (and all non-"Basic" routes like `Route.LoggedInRequiresOrgWithBanking`
// and `Route.Admin` components expect & require the key to be present in the component's props type).
//
// This allows the compiler to detect if we used `Route.LoggedIn` where we should have used `Route.LoggedInRequiresOrgWithBanking`
// or `Route.Admin`, and vice versa.
//
// this type `TPropsForBasicRouteComponents` also allows "Basic" routes like `Route.LoggedIn`'s components to not
// specify any need for `RouteComponentProps` in their props, for those simpler pages that don't need them
// (via `...WithoutRouteComponentProps` below)
//
// (this is something we've chosen not to allow in `Route.LoggedInRequiresOrgWithBanking` and `Route.Admin` for now for clarity)
export type TPropsForBasicRouteComponents =
  | TPropsForBasicRouteComponentWithRouteComponentProps
  | TPropsForBasicRouteComponentWithoutRouteComponentProps

export type TPropsForBasicRouteComponentWithoutRouteComponentProps = {
  [ROUTE_TYPE_KEY]?: undefined
}

export type TPropsForBasicRouteComponentWithRouteComponentProps =
  RouteComponentProps & TPropsForBasicRouteComponentWithoutRouteComponentProps
